/**
 * Login View — the Inbox splash/login screen.
 *
 * Shows the Inbox branding and a Google sign-in button
 * via Supabase OAuth. Unauthorised users see an access-denied message.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';

export default function Login() {
  const { login, isAuthenticated, isAuthorised, isLoading, user } = useAuth();

  // Authenticated but not authorised — access denied
  const showAccessDenied = isAuthenticated && !isAuthorised;

  return (
    <div style={styles.page} data-celebration="login-bg">
      <div style={styles.overlay} />
      <motion.div
        style={styles.card}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      >
        {/* Inbox Branding */}
        <div style={styles.branding}>
          <div style={styles.cpLogo}>
            <svg width="40" height="40" viewBox="0 0 110 110" xmlns="http://www.w3.org/2000/svg">
              <rect x="0" y="0" width="110" height="110" rx="22" fill="#0F4C75"/>
              <rect x="12" y="62" width="86" height="38" rx="6" fill="#1B9AAA" opacity="0.5"/>
              <rect x="12" y="82" width="86" height="18" rx="5" fill="#1B9AAA" opacity="0.4"/>
              <rect x="42" y="89" width="26" height="7" rx="3.5" fill="#F4A261"/>
              <rect x="24" y="18" width="62" height="72" rx="5" fill="white"/>
              <polygon points="70,18 86,18 86,34" fill="#0F4C75" opacity="0.2"/>
              <rect x="32" y="34" width="26" height="5" rx="2.5" fill="#F4A261"/>
              <rect x="32" y="46" width="38" height="3" rx="1.5" fill="#0F4C75" opacity="0.3"/>
              <rect x="32" y="55" width="32" height="3" rx="1.5" fill="#0F4C75" opacity="0.3"/>
              <rect x="32" y="64" width="28" height="3" rx="1.5" fill="#0F4C75" opacity="0.3"/>
            </svg>
            <h1 style={styles.cpTitle}>Inbox</h1>
          </div>
          <p style={styles.tagline}>Workflow Management System</p>
        </div>

        {showAccessDenied ? (
          <motion.div
            style={styles.accessDenied}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <ShieldAlert size={40} color="#C0392B" />
            <h2 style={styles.deniedTitle}>Access Denied</h2>
            <p style={styles.deniedText}>
              The account <strong>{user?.email}</strong> is not authorised
              to access Inbox.
            </p>
            <p style={styles.deniedSubtext}>
              Only approved team members can use this application.
              Please contact your administrator if you believe this is an error.
            </p>
          </motion.div>
        ) : (
          <motion.button
            style={styles.loginBtn}
            onClick={login}
            disabled={isLoading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {isLoading ? 'Signing in...' : 'Sign in with Google'}
          </motion.button>
        )}

        {/* Compliance footer */}
        <div style={styles.compliance}>
          <p>Inbox © 2025 — Private & Confidential</p>
        </div>
      </motion.div>
    </div>
  );
}

const styles = {
  page: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, var(--cp-primary) 0%, var(--cp-secondary) 50%, var(--cp-primary) 100%)',
    position: 'relative',
    padding: '1rem',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(ellipse at 30% 20%, rgba(244, 162, 97, 0.15) 0%, transparent 60%)',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative',
    background: 'rgba(255, 255, 255, 0.97)',
    borderRadius: '20px',
    padding: '3rem 2.5rem',
    maxWidth: '440px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
  },
  branding: {
    marginBottom: '2rem',
  },
  cpLogo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.6rem',
    marginBottom: '0.3rem',
  },
  cpTitle: {
    fontSize: '1.8rem',
    fontWeight: 700,
    color: 'var(--cp-primary)',
    margin: 0,
    letterSpacing: '-0.02em',
  },
  tagline: {
    color: 'var(--cp-text-muted)',
    fontSize: '0.9rem',
    marginTop: '0.25rem',
  },
  loginBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.6rem',
    padding: '0.85rem 2rem',
    background: 'var(--cp-surface)',
    color: 'var(--cp-text)',
    border: '1.5px solid var(--cp-border)',
    borderRadius: '10px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
    marginBottom: '2rem',
    transition: 'all 0.2s',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  },
  accessDenied: {
    margin: '1rem 0 2rem',
    padding: '1.5rem',
    background: 'rgba(192, 57, 43, 0.06)',
    borderRadius: '12px',
    border: '1px solid rgba(192, 57, 43, 0.15)',
  },
  deniedTitle: {
    color: 'var(--cp-danger)',
    fontSize: '1.1rem',
    marginTop: '0.75rem',
    marginBottom: '0.5rem',
  },
  deniedText: {
    color: 'var(--cp-text)',
    fontSize: '0.9rem',
    marginBottom: '0.5rem',
  },
  deniedSubtext: {
    color: 'var(--cp-text-muted)',
    fontSize: '0.8rem',
  },
  compliance: {
    borderTop: '1px solid var(--cp-border)',
    paddingTop: '1rem',
    fontSize: '0.65rem',
    color: 'var(--cp-text-muted)',
    lineHeight: 1.5,
  },
};
